import { useBrokers } from '@/hooks/useBrokers';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SHOWN_ASSET_CLASSES,
  type AssetClass,
  type AssetFilter,
  type Broker,
  NO_ASSET_FILTER,
  assetFilterActive,
  instrumentOf,
  useInstruments,
  useMarketHours,
} from '@/lib/instruments';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { intlLocale } from '@/lib/format';
import { useAssets } from '@/hooks/useAssets';

/*
 * Asset types on screen (lib/instruments.ts, 2026-10-04): the type beside
 * every asset, the asset without "USDT", and the two filters -- asset type,
 * and "available on" a broker (Phemex; KuCoin for admins until it is live).
 */

// Guillaume, 2026-10-04: crypto orange, stock blue, commodities green.
const CLASS_STYLE: Record<AssetClass, string> = {
  crypto: 'border-orange-500/40 text-orange-400',
  stock: 'border-blue-500/40 text-blue-400',
  commodity: 'border-green-500/40 text-green-400',
  indicator: 'border-purple-500/40 text-purple-400',
  forex: 'border-slate-500/40 text-slate-400',
};

/** The small type label beside an asset. */
export function AssetClassBadge({ cls, className = '' }: { cls: AssetClass; className?: string }) {
  const { t } = useTranslation('common');
  // Forex is not shown until a broker can execute it (2026-10-09).
  if (cls === 'forex') return null;
  return (
    <span
      className={`inline-flex items-center rounded border px-1 py-px text-[9px] font-medium uppercase leading-none tracking-wide ${CLASS_STYLE[cls]} ${className}`}
    >
      {t(`asset.class.${cls}`)}
    </span>
  );
}

/** An asset as a person reads it: BTC, TSLA, GOLD -- with its type beside it.
 *  Memoised: tables re-render on every price tick, and the label of a row
 *  whose symbol did not change has nothing to redraw. */
export const SymbolLabel = memo(function SymbolLabel({
  symbol,
  badge = true,
  className = '',
  traderUid,
  showName = true,
}: {
  symbol: string | null | undefined;
  badge?: boolean;
  className?: string;
  /** "XAU (Gold)": the ticker and, small beside it, what it is (Guillaume,
   *  2026-10-06: not everybody knows XAU is gold). Off where space is short. */
  showName?: boolean;
  /** The trade's trader: a name can mean another instrument on his venue (MEXC's CATUSDT). */
  traderUid?: string | null;
}) {
  const { data: reg } = useInstruments();
  const info = instrumentOf(symbol, reg, traderUid);
  if (!symbol) return <span className={className}>—</span>;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} title={info.name ? `${info.name} · ${info.display}` : info.display}>
      <span>{info.display}</span>
      {showName && info.name && (
        <span className="text-[0.8em] font-normal text-muted-foreground truncate max-w-[8rem]">({info.name})</span>
      )}
      {badge && <AssetClassBadge cls={info.class} />}
    </span>
  );
});

/****************
 * "NYSE closed: the price can gap at the open". 2026-10-06.
 *
 * Guillaume asked what market hours are worth on contracts that trade 24/7.
 * Not the hours: the price risk. While the market behind a stock or
 * commodity perpetual is shut, the perpetual trades on thin books with
 * nothing under it and can jump at the reopen. So this appears only then,
 * only for an asset with a home market, beside an open position or on the
 * asset's page. The calendar is the server's (markethours), the same one the
 * Strategy Builder's "don't open while closed" rule reads.
 ****************/
export function MarketClosedBadge({ symbol, className = '' }: { symbol: string | null | undefined; className?: string }) {
  const { t } = useTranslation('common');
  const { data: reg } = useInstruments();
  const { data: hours } = useMarketHours();
  if (!symbol) return null;
  const market = instrumentOf(symbol, reg).market;
  const st = market ? hours?.get(market) : undefined;
  if (!st || st.open) return null;
  const opens = st.next_change
    ? new Date(st.next_change).toLocaleString(intlLocale(), { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-flex items-center rounded border border-amber-500/40 text-amber-400 px-1 py-px text-[9px] font-medium leading-none shrink-0 cursor-help ${className}`}>
          {t('asset.marketClosed.badge', { market: st.name })}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-xs">
        {t('asset.marketClosed.tip', { market: st.name, opens })}
      </TooltipContent>
    </Tooltip>
  );
}

const SELECT =
  'h-7 rounded-md border border-white/10 bg-background text-foreground px-2 text-[11px] [&>option]:bg-background [&>option]:text-foreground';

/** Asset type and broker availability, side by side. */
export function AssetFilterControls({
  value,
  onChange,
  className = '',
  part = 'both',
  showClear = true,
  withAsset = false,
}: {
  value: AssetFilter;
  onChange: (f: AssetFilter) => void;
  className?: string;
  /** One control alone, to place another between them (the trader page's asset picker). */
  part?: 'type' | 'broker' | 'both';
  showClear?: boolean;
  /** One asset as well (the leaderboard, 2026-10-06): rank traders on that asset's trades. */
  withAsset?: boolean;
}) {
  const { t } = useTranslation('common');
  const visible = useBrokers().visible;
  const brokers = (['phemex', 'kucoin'] as Broker[]).filter(visible);
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {part !== 'broker' && <select
        value={value.assetClass}
        onChange={(e) => onChange({ ...value, assetClass: e.target.value as AssetFilter['assetClass'], asset: undefined })}
        className={SELECT}
        aria-label={t('asset.filter.type')}
      >
        <option value="all">{t('asset.filter.allTypes')}</option>
        {SHOWN_ASSET_CLASSES.map((c) => (
          <option key={c} value={c}>
            {t(`asset.split.${c}`)}
          </option>
        ))}
      </select>}
      {part !== 'type' && <select
        value={value.broker}
        onChange={(e) => onChange({ ...value, broker: e.target.value as AssetFilter['broker'] })}
        className={SELECT}
        aria-label={t('asset.filter.broker')}
        title={t('asset.filter.brokerTitle')}
      >
        <option value="any">{t('asset.filter.anyBroker')}</option>
        {brokers.map((b) => (
          <option key={b} value={b}>
            {t(`asset.broker.${b}`)}
          </option>
        ))}
      </select>}
      {withAsset && <AssetPicker value={value} onChange={onChange} />}
      {showClear && part === 'both' && assetFilterActive(value) && (
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline hover:text-foreground"
          onClick={() => onChange(NO_ASSET_FILTER)}
        >
          {t('asset.filter.clear')}
        </button>
      )}
    </div>
  );
}

/*
 * One asset, picked by name or ticker among the chosen type's assets, most
 * traded first. "Who is best on gold" with every leaderboard filter, in the
 * leaderboard (Guillaume, 2026-10-06: "have it all in once place").
 */
function AssetPicker({ value, onChange }: { value: AssetFilter; onChange: (f: AssetFilter) => void }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useAssets();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const assets = data?.assets;
  const chosen = useMemo(() => assets?.find((a) => a.slug === value.asset), [assets, value.asset]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Until the list arrives there is nothing to filter: the menu says it is
    // loading (below) rather than showing an empty list that reads as "none".
    if (!assets) return [];
    return assets
      .filter((a) => value.assetClass === 'all' || a.class === value.assetClass)
      .filter((a) => !s || a.display.toLowerCase().includes(s) || a.name.toLowerCase().includes(s))
      .sort((a, b) => b.traders - a.traders)
      .slice(0, 40);
  }, [assets, q, value.assetClass]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${SELECT} inline-flex items-center gap-1`} aria-label={t('asset.filter.asset')}>
          {chosen ? <>{chosen.display}<AssetClassBadge cls={chosen.class} /></> : t('asset.filter.anyAsset')}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('asset.filter.searchAsset')}
          className="h-7 w-full rounded-md border border-white/10 bg-background px-2 text-[11px] mb-1.5"
        />
        <div className="max-h-60 overflow-y-auto space-y-0.5">
          {isLoading && !assets && <p className="text-[11px] text-muted-foreground px-1.5 py-1">{t('asset.filter.loadingAssets')}</p>}
          {value.asset && (
            <button type="button" className="w-full text-start text-[11px] px-1.5 py-1 rounded hover:bg-muted/50 text-muted-foreground"
              onClick={() => { onChange({ ...value, asset: undefined }); setOpen(false); }}>
              {t('asset.filter.anyAsset')}
            </button>
          )}
          {list.map((a) => (
            <button key={a.slug} type="button"
              className={`w-full flex items-center gap-1.5 text-start text-[11px] px-1.5 py-1 rounded hover:bg-muted/50 ${a.slug === value.asset ? 'bg-muted/60' : ''}`}
              onClick={() => { onChange({ ...value, asset: a.slug }); setOpen(false); setQ(''); }}>
              <span className="font-medium">{a.display}</span>
              <span className="text-muted-foreground truncate flex-1">{a.name}</span>
              <AssetClassBadge cls={a.class} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The asset split's own switch: all, or one type. */
export function AssetSplitSwitch({
  value,
  onChange,
  present,
  size = 'sm',
}: {
  /** 'md': the height of a page's other selectors (the Traders page, round 8 #215). */
  size?: 'sm' | 'md';
  value: AssetClass | 'all';
  onChange: (v: AssetClass | 'all') => void;
  /** Types present in the data; forex is offered only when some is. */
  present?: Set<AssetClass>;
}) {
  const { t } = useTranslation('common');
  // Forex stays in the register, out of the menus until it is offered (2026-10-04).
  void present;
  const options: (AssetClass | 'all')[] = ['all', 'crypto', 'stock', 'commodity'];
  return (
    <div className={size === 'md' ? 'inline-flex items-center gap-1 glass rounded-lg p-1 h-9' : 'inline-flex rounded-md border border-white/10 p-0.5'}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`${size === 'md' ? 'px-3 h-7 text-xs font-medium' : 'px-1.5 py-0.5 text-[10px]'} rounded ${value === o ? (size === 'md' ? 'bg-primary text-primary-foreground' : 'bg-white/10 text-foreground') : 'text-muted-foreground hover:text-foreground'}`}
        >
          {t(`asset.split.${o}`)}
        </button>
      ))}
    </div>
  );
}
